/* External dependencies */
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { ICognitoUserPoolData } from 'amazon-cognito-identity-js';

/* Local dependencies */
import { UserRole } from '../../../common';

export enum AuthActionTypes {
  INIT_CLIENT = 'INIT_CLIENT',
  INIT_CLIENT_FAILED = 'INIT_CLIENT_FAILED',
  INIT_CLIENT_SUCCEEDED = 'INIT_CLIENT_SUCCEEDED',
  INIT_ANONYMOUS_CLIENT = 'INIT_ANONYMOUS_CLIENT',
  LOGOUT_REQUEST = 'LOGOUT_REQUEST',
  LOGOUT_SUCCESS = 'LOGOUT_SUCCESS',
  LOGOUT_ERROR = 'LOGOUT_ERROR',
  SET_AUTH_VIEW_MODE = 'SET_AUTH_VIEW_MODE',
  SEND_VERIFICATION_CODE_REQUEST = 'SEND_VERIFICATION_CODE_REQUEST',
  SET_USER_SESSION = 'SET_USER_SESSION',
  CONFIRM_REGISTRATION_REQUEST = 'CONFIRM_REGISTRATION_REQUEST',
  CONFIRM_REGISTRATION_SUCCESS = 'CONFIRM_REGISTRATION_SUCCESS',
  CONFIRM_REGISTRATION_ERROR = 'CONFIRM_REGISTRATION_ERROR',
  SET_CONFIRMATION_CODE = 'SET_CONFIRMATION_CODE',
  RESET_AUTH_STATE = 'RESET_AUTH_STATE',
  SEND_AUTH_ERROR = 'SEND_AUTH_ERROR',
}

export type AuthViewMode =
  | 'login'
  | 'register'
  | 'forgotPassword'
  | 'forgotPasswordVerify'
  | 'forgotPasswordReset'
  | 'verify'
  | 'dashboard';

export interface SessionUser {
  sub?: string;
  roles?: UserRole[];
}

export interface InitClient {
  type: AuthActionTypes.INIT_CLIENT;
}

export interface InitClientFailed {
  type: AuthActionTypes.INIT_CLIENT_FAILED;
}

export interface InitClientSucceeded {
  type: AuthActionTypes.INIT_CLIENT_SUCCEEDED;
  session: any;
}

export interface InitAnonymousClient {
  type: AuthActionTypes.INIT_ANONYMOUS_CLIENT;
  client: ApolloClient<NormalizedCacheObject>;
}

export interface LogoutRequest {
  type: AuthActionTypes.LOGOUT_REQUEST;
}

export interface LogoutError {
  type: AuthActionTypes.LOGOUT_ERROR;
  error: any;
}

export interface LogoutSuccess {
  type: AuthActionTypes.LOGOUT_SUCCESS;
}

export interface SetAuthViewMode {
  type: AuthActionTypes.SET_AUTH_VIEW_MODE;
  mode: AuthViewMode;
}

export interface SendVerificationCodeRequest {
  type: AuthActionTypes.SEND_VERIFICATION_CODE_REQUEST;
  username: string;
  userPool: ICognitoUserPoolData;
  currentUserSession: any;
  verificationCode?: string;
}

export interface SetUserSession {
  type: AuthActionTypes.SET_USER_SESSION;
  session: any;
}

export interface ConfirmRegistrationRequest {
  type: AuthActionTypes.CONFIRM_REGISTRATION_REQUEST;
  username: string;
  confirmationCode: string;
  userPool: ICognitoUserPoolData;
}

export interface ConfirmRegistrationSuccess {
  type: AuthActionTypes.CONFIRM_REGISTRATION_SUCCESS;
}

export interface ConfirmRegistrationError {
  type: AuthActionTypes.CONFIRM_REGISTRATION_ERROR;
  error: any;
}

export interface SetConfirmationCode {
  type: AuthActionTypes.SET_CONFIRMATION_CODE;
  code: string;
}

export interface ResetAuthState {
  type: AuthActionTypes.RESET_AUTH_STATE;
}

export interface SendAuthError {
  type: AuthActionTypes.SEND_AUTH_ERROR;
  error: Record<string, Error | undefined> | Error;
}

export type AuthAction =
  | InitClient
  | InitClientFailed
  | InitClientSucceeded
  | InitAnonymousClient
  | LogoutRequest
  | LogoutError
  | LogoutSuccess
  | SetAuthViewMode
  | SendVerificationCodeRequest
  | SetUserSession
  | ConfirmRegistrationRequest
  | ConfirmRegistrationSuccess
  | ConfirmRegistrationError
  | SetConfirmationCode
  | ResetAuthState
  | SendAuthError;
